import { Button } from "@/components/ui/button";
import { Footer } from "@/components/Footer";
import { ArrowLeft, Heart, Sparkles, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import SEO from "@/components/SEO";

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO
        title="About Her Season — our story and mission"
        description="Why we built Her Season: a warm, science-backed companion for perimenopause and menopause, written for real women navigating this chapter of life."
      />
      {/* Header */}
      <header className="border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <p className="text-xl font-semibold">Our Story</p>
          <div className="w-10" />
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Brewing Confidence, One Sip at a Time
          </h1>
          <p className="text-xl text-muted-foreground">
            We believe every woman deserves to feel empowered, no matter what hormones throw their way.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 px-4 bg-secondary/20">
        <div className="container mx-auto max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <Heart className="h-8 w-8 text-primary" />
            <h3 className="text-3xl font-bold">Our Mission</h3>
          </div>
          <p className="text-lg leading-relaxed mb-4">
            Our mission is to create products that celebrate the real, raw, and often hilarious journey of navigating hormonal changes, perimenopause, and menopause. We're here to remind you that you're not alone, you're not "too much," and yes, that mood swing is totally valid.
          </p>
          <p className="text-lg leading-relaxed">
            Every mug is designed to spark joy, start conversations, and serve as a daily reminder that you're doing amazing—even on the days when you forget why you walked into the room.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <Sparkles className="h-8 w-8 text-primary" />
            <h3 className="text-3xl font-bold">Our Story</h3>
          </div>
          <p className="text-lg leading-relaxed mb-4">
            It started with a simple observation: why do we whisper about hot flashes but shout about everything else? Why do we apologize for mood swings instead of owning them? And most importantly, why wasn't there a coffee mug that said "My Hormones Are Brewing" when we needed it most?
          </p>
          <p className="text-lg leading-relaxed mb-4">
            So we created one. And then another. And another. Each design born from real experiences, real frustrations, and real laughs shared between women who get it.
          </p>
          <p className="text-lg leading-relaxed">
            We're not doctors or wellness gurus—we're just people who believe that humor, community, and a really good cup of coffee can make the journey a whole lot better.
          </p>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 px-4 bg-secondary/20">
        <div className="container mx-auto max-w-4xl">
          <div className="flex items-center gap-3 mb-8">
            <Users className="h-8 w-8 text-primary" />
            <h3 className="text-3xl font-bold">What We Stand For</h3>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h4 className="text-xl font-semibold mb-3">Authenticity</h4>
              <p className="text-muted-foreground">
                No sugarcoating, no pretending. Just real talk about real experiences.
              </p>
            </div>
            <div>
              <h4 className="text-xl font-semibold mb-3">Empowerment</h4>
              <p className="text-muted-foreground">
                Your feelings are valid. Your experience matters. You deserve products that celebrate that.
              </p>
            </div>
            <div>
              <h4 className="text-xl font-semibold mb-3">Community</h4>
              <p className="text-muted-foreground">
                We're building a space where women can laugh, relate, and feel seen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-2xl text-center">
          <h3 className="text-3xl font-bold mb-4">Join the Movement</h3>
          <p className="text-lg text-muted-foreground mb-8">
            Ready to sip, smile, and own your journey?
          </p>
          <Button size="lg" onClick={() => navigate("/shop")}>
            Shop Our Collection
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
